export const SYNC_BACKOFF_MS = Object.freeze([
  2000,
  5000,
  15000,
  30000,
  60000,
  120000,
]);

export class SyncScheduler {
  constructor({
    onTrigger,
    windowObject = typeof window === "undefined" ? null : window,
    documentObject = typeof document === "undefined" ? null : document,
    setTimeoutImpl = setTimeout,
    clearTimeoutImpl = clearTimeout,
  } = {}) {
    this.onTrigger = onTrigger || (() => {});
    this.windowObject = windowObject;
    this.documentObject = documentObject;
    // Invoke timers unbound: native setTimeout/clearTimeout throw
    // "Illegal invocation" in browsers when called with a non-global `this`.
    this.setTimeoutImpl = (...args) => setTimeoutImpl(...args);
    this.clearTimeoutImpl = (...args) => clearTimeoutImpl(...args);
    this.retryIndex = 0;
    this.retryTimer = null;
    this.performedTimer = null;
    this.started = false;
    this.handleOnline = () => this.trigger("online");
    this.handleVisibility = () => {
      if (this.documentObject?.visibilityState === "visible") {
        this.trigger("visible");
      }
    };
  }

  invoke(reason, context) {
    Promise.resolve(this.onTrigger({ reason, ...context })).catch(() => {});
  }

  start() {
    if (this.started) return;
    this.started = true;
    this.windowObject?.addEventListener("online", this.handleOnline);
    this.documentObject?.addEventListener("visibilitychange", this.handleVisibility);
    this.trigger("app-start");
  }

  stop() {
    this.started = false;
    this.windowObject?.removeEventListener("online", this.handleOnline);
    this.documentObject?.removeEventListener("visibilitychange", this.handleVisibility);
    this.clear();
  }

  clear() {
    if (this.retryTimer != null) this.clearTimeoutImpl(this.retryTimer);
    if (this.performedTimer != null) this.clearTimeoutImpl(this.performedTimer);
    this.retryTimer = null;
    this.performedTimer = null;
  }

  resetBackoff() {
    this.retryIndex = 0;
    if (this.retryTimer != null) this.clearTimeoutImpl(this.retryTimer);
    this.retryTimer = null;
  }

  trigger(reason, context = {}) {
    this.resetBackoff();
    if (reason === "performed-set") {
      if (this.performedTimer != null) this.clearTimeoutImpl(this.performedTimer);
      this.performedTimer = this.setTimeoutImpl(() => {
        this.performedTimer = null;
        this.invoke(reason, context);
      }, 2000);
      return;
    }
    this.invoke(reason, context);
  }

  scheduleRetry(retryAfterMs = null, context = {}) {
    if (this.documentObject?.visibilityState === "hidden") return null;
    if (this.retryTimer != null) return this.retryTimer;
    const canonicalDelay = SYNC_BACKOFF_MS[
      Math.min(this.retryIndex, SYNC_BACKOFF_MS.length - 1)
    ];
    const delay = Math.max(canonicalDelay, Number(retryAfterMs) || 0);
    this.retryIndex = Math.min(this.retryIndex + 1, SYNC_BACKOFF_MS.length - 1);
    this.retryTimer = this.setTimeoutImpl(() => {
      this.retryTimer = null;
      this.invoke("retry", context);
    }, delay);
    return delay;
  }
}
